import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus, FileCheck, X } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { FormData, validateStep } from '../utils/formValidation';
import PersonalInformationStep from './registration/PersonalInformationStep';
import AccountTypeStep from './registration/AccountTypeStep';
import DojoSetupStep from './registration/DojoSetupStep';
import DojoCodeStep from './registration/DojoCodeStep';
import EmergencyContactStep from './registration/EmergencyContactStep';
import MartialArtsBackgroundStep from './registration/MartialArtsBackgroundStep';
import DisclaimerStep from './registration/DisclaimerStep';
import RegistrationCodeStep from './registration/RegistrationCodeStep';

const StudentRegistrationForm = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationError, setRegistrationError] = useState(''); // State for general registration errors
  const [selectedDojo, setSelectedDojo] = useState<{ id: string | null; name: string | null }>({ id: null, name: null }); // State to hold selected dojo info
  const [formData, setFormData] = useState<FormData>({
    // Personal Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Canada',
    customCountry: '',
    role: 'student', // Default to student

    // Admin Registration Code
    registrationCode: '',
    registrationCodeValid: false,

    // Dojo Setup (for admin signups)
    dojoName: '',
    martialArtType: 'Karate',
    martialArtStyle: 'Kyokushin',
    joinCode: '',

    // Dojo Code (for student signups)
    dojoCode: '',
    dojoCodeValid: false,
    studentDojoName: '',

    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',

    // Medical Information
    medicalConditions: '',
    currentMedications: '',
    allergies: '',
    injuries: '',
    doctorName: '',
    doctorPhone: '',

    // Martial Arts Background
    previousExperience: 'none',
    previousStyles: '',
    yearsOfExperience: '',
    previousBelt: '',

    // Preferences
    trainingGoals: '',
    howDidYouHear: '',

    // Disclaimer
    disclaimerAgreed: false,
    digitalSignature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [registrationCodeError, setRegistrationCodeError] = useState('');
  const [dojoCodeError, setDojoCodeError] = useState('');
  const [adminType, setAdminType] = useState<'create' | 'join'>('create');
  const [adminDojoCode, setAdminDojoCode] = useState('');
  const [adminDojoName, setAdminDojoName] = useState('');
  const totalSteps = 6; // Updated to 6 steps for both admin and student

  const updateFormData = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    console.log('nextStep called, currentStep:', currentStep, 'role:', formData.role, 'adminType:', adminType);
    console.log('adminDojoCode:', adminDojoCode, 'adminDojoName:', adminDojoName);

    // Special validation for admin step (step 2 when role is admin)
    if (currentStep === 2 && formData.role === 'admin') {
      // Only validate dojo code when joining existing dojo
      if (adminType === 'join' && (!adminDojoCode || !adminDojoName)) {
        console.log('Failed: dojo code validation', { adminDojoCode, adminDojoName });
        toast({
          title: uiText("Invalid Dojo Code"),
          description: uiText("Please provide a valid dojo code to proceed."),
          variant: "destructive"
        });
        return;
      }

      // If admin is joining existing dojo, skip dojo setup step (step 3)
      if (adminType === 'join') {
        setCurrentStep(4); // Skip to step 4 (Emergency Contact)
        return;
      }
    }

    if (validateStep(currentStep, formData)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      toast({
        title: uiText("Incomplete Information"),
        description: uiText("Please fill in all required fields before proceeding."),
        variant: "destructive"
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(totalSteps, formData)) {
      toast({
        title: uiText("Registration Incomplete"),
        description: uiText("Please complete all required fields and sign the disclaimer."),
        variant: "destructive"
      });
      return;
    }

    // Additional validation for student registrations
    if (formData.role === 'student') {
      if (!formData.dojoCode || !formData.dojoCodeValid) {
        toast({
          title: uiText("Dojo Code Required"),
          description: uiText("A valid dojo code is required for all student registrations."),
          variant: "destructive"
        });
        return;
      }
    }

    // Additional validation for admin registrations joining existing dojos
    if (formData.role === 'admin') {
      if (adminType === 'join' && (!adminDojoCode || !adminDojoName)) {
        toast({
          title: uiText("Invalid Dojo Code"),
          description: uiText("A valid dojo code is required for joining an existing dojo as admin."),
          variant: "destructive"
        });
        return;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: uiText("Password Mismatch"),
        description: uiText("Passwords do not match. Please check and try again."),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    setRegistrationError(''); // Clear previous errors

    try {
      console.log('Starting registration process for:', formData.email);
      console.log('Role being assigned:', formData.role);

      // Prepare submission data based on role
      let submissionData = {
        // Common fields
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        role: formData.role || 'student',
        date_of_birth: formData.dateOfBirth,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postalCode,
        country: formData.country,
        // For admin users
        ...(formData.role === 'admin' ? {
          // Creating new dojo
          ...(adminType === 'create' ? {
            dojo_name: formData.dojoName,
            martial_art_type: formData.martialArtType,
            martial_art_style: formData.martialArtStyle
          } : {
            // Joining existing dojo with dojo code
            dojo_code: adminDojoCode
          })
        } : {
          // For students, dojo code is MANDATORY
          dojo_code: formData.dojoCode
        })
      };

      console.log('Registration data being submitted:', submissionData);

      // Validate required fields before submission
      const requiredFields = ['email', 'password', 'firstName', 'lastName', 'dateOfBirth', 'phone'];
      const missingFields = requiredFields.filter(field => {
        const value = formData[field as keyof FormData];
        return !value || (typeof value === 'string' && value.trim() === '');
      });

      if (missingFields.length > 0) {
        setRegistrationError(`Missing required fields: ${missingFields.join(', ')}`);
        setIsSubmitting(false); // Ensure submitting state is reset
        return;
      }

      // Ensure dojo_id is properly set for students
      if (formData.role === 'student' && (!formData.dojoCode || !formData.dojoCodeValid)) {
        setRegistrationError('Please enter and validate a Dojo Code.');
        setIsSubmitting(false);
        return;
      }

      // Clean and prepare the user data for Supabase auth.signUp
      const cleanSubmissionData = {
        ...submissionData,
        email: formData.email.toLowerCase().trim(),
        // Map form fields to Supabase column names
        ...(formData.role === 'admin' ? {
          ...(adminType === 'create' ? {
            dojo_name: formData.dojoName?.trim(),
            martial_art_type: formData.martialArtType,
            martial_art_style: formData.martialArtStyle
          } : {
            // When joining, dojo_code is provided directly
            dojo_code: adminDojoCode?.trim()
          })
        } : {
          dojo_code: formData.dojoCode?.trim()
        }),
        // Add student-specific dojo linking if applicable
        ...(formData.role === 'student' && formData.studentDojoName && formData.dojoCodeValid && {
          student_dojo_name: formData.studentDojoName.trim()
        })
      };

      console.log('Clean registration data:', cleanSubmissionData);

      const { data, error } = await supabase.auth.signUp({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        options: {
          data: cleanSubmissionData,
          emailRedirectTo: `${window.location.origin}/confirm-signup`
        }
      });

      if (error) {
        console.error('Registration error:', error);
        // Provide more specific error messages
        if (error.message.includes('already registered')) {
          setRegistrationError('This email is already registered. Please use a different email or try logging in.');
        } else if (error.message.includes('invalid email')) {
          setRegistrationError('Please enter a valid email address.');
        } else if (error.message.includes('password')) {
          setRegistrationError('Password must be at least 6 characters long.');
        } else {
          setRegistrationError(`Registration failed: ${error.message}`);
        }
        setIsSubmitting(false); // Ensure submitting state is reset
        return;
      }

      console.log('Registration response:', data);

      // Check if user needs email confirmation
      if (data.user && !data.session) {
        // User was created but needs email confirmation
        const accountType = formData.role === 'admin' ? 'Administrator' : 'Student';
        toast({
          title: uiText("Registration Successful!"),
          description: uiText("Welcome to the Dojo! You've been registered as a {{value0}}. Please check your email and click the confirmation link before logging in.", { value0: accountType }),
          duration: 8000
        });

        // Redirect to login with a message
        setTimeout(() => {
          navigate('/login?message=check-email');
        }, 3000);
        return;
      }

      // If we have a session, the user is automatically logged in
      if (data.session && data.user) {
        console.log('User automatically logged in after registration');

        // Save additional data if we have a session
        try {
          const userId = data.user.id;

          // Save emergency contact
          if (formData.emergencyContactName && formData.emergencyContactPhone) {
            const { error: emergencyError } = await supabase
              .from('emergency_contacts')
              .insert({
                user_id: userId,
                name: formData.emergencyContactName,
                phone: formData.emergencyContactPhone,
                relationship: formData.emergencyContactRelationship
              });

            if (emergencyError) {
              console.error('Error saving emergency contact:', emergencyError);
            }
          }

          // Save medical information
          const { error: medicalError } = await supabase
            .from('medical_info')
            .insert({
              user_id: userId,
              medical_conditions: formData.medicalConditions,
              current_medications: formData.currentMedications,
              allergies: formData.allergies,
              injuries: formData.injuries,
              doctor_name: formData.doctorName,
              doctor_phone: formData.doctorPhone
            });

          if (medicalError) {
            console.error('Error saving medical info:', medicalError);
          }

          // Save martial arts background
          const { error: backgroundError } = await supabase
            .from('martial_arts_background')
            .insert({
              user_id: userId,
              previous_experience: formData.previousExperience,
              previous_styles: formData.previousStyles,
              years_of_experience: formData.yearsOfExperience,
              previous_belt: formData.previousBelt,
              training_goals: formData.trainingGoals,
              how_did_you_hear: formData.howDidYouHear
            });

          if (backgroundError) {
            console.error('Error saving martial arts background:', backgroundError);
          }

          // Clean up completed - no registration code handling needed for new dojos


        } catch (additionalDataError) {
          console.error('Error saving additional registration data:', additionalDataError);
          // Don't fail the registration for this - the user is already created
        }

        // Send confirmation email for admin users
        if (formData.role === 'admin') {
          try {
            const confirmationData = {
              name: `${formData.firstName} ${formData.lastName}`,
              email: formData.email,
              dojoName: adminType === 'join' ? adminDojoName : formData.dojoName,
              adminType: adminType
            };

            await supabase.functions.invoke('send-admin-confirmation', {
              body: confirmationData
            });
          } catch (emailError) {
            console.error('Error sending confirmation email:', emailError);
            // Don't fail the registration for email errors
          }
        }

        // Log the user out and redirect to login page
        await supabase.auth.signOut();

        const accountType = formData.role === 'admin' ? 'Administrator' : 'Student';
        const dojoMessage = formData.role === 'admin' && adminType === 'join'
          ? ` You are now an admin of ${adminDojoName}.`
          : '';

        toast({
          title: uiText("Registration Successful!"),
          description: uiText("Welcome to the Dojo! You've been registered as a {{value0}}.{{value1}} A confirmation email has been sent to you. Please log in to access your account.", { value0: accountType, value1: dojoMessage })
        });

        // Redirect to login page
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }

    } catch (error) {
      console.error('Unexpected registration error:', error);
      toast({
        title: uiText("Registration Error"),
        description: uiText("An unexpected error occurred. Please try again."),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const isAdminRole = formData.role === 'admin';

    switch (currentStep) {
      case 1: // Personal Information
        return <PersonalInformationStep formData={formData} updateFormData={updateFormData} />;

      case 2: // Account Type Selection
        return (
          <AccountTypeStep
            role={formData.role}
            setRole={(role) => {
              updateFormData('role', role);
              // Reset dojo-related states when switching role
              if (role === 'student') {
                setAdminType('create'); // Default to 'create' for student
                setAdminDojoCode('');
                setAdminDojoName('');
                setSelectedDojo({ id: null, name: null });
              } else {
                setAdminDojoCode(''); // Clear admin dojo code if switching to admin
                setAdminDojoName('');
                setSelectedDojo({ id: null, name: null });
              }
            }}
            registrationCode={formData.registrationCode || ''}
            setRegistrationCode={(code) => updateFormData('registrationCode', code)}
            isValid={formData.registrationCodeValid || false}
            setIsValid={(valid) => updateFormData('registrationCodeValid', valid)}
            error={registrationCodeError}
            setError={setRegistrationCodeError}
            dojoCode={adminDojoCode}
            setDojoCode={setAdminDojoCode}
            dojoName={adminDojoName}
            setDojoName={setAdminDojoName}
            adminType={adminType}
            setAdminType={setAdminType}
            setSelectedDojo={setSelectedDojo} // Pass setSelectedDojo here
          />
        );

      case 3: // Dojo Setup (Admin) or Dojo Code (Student)
        if (isAdminRole) {
          // If admin is joining an existing dojo, skip to next relevant step
          if (adminType === 'join') {
            // Logic to skip to step 4 or 5 if dojo code is already validated
            // For now, we'll let the navigation handle it
            return <div />; // Render nothing and let nextStep handle the transition
          }
          return (
            <DojoSetupStep
              dojoName={formData.dojoName || ''}
              setDojoName={(name) => updateFormData('dojoName', name)}
              martialArtType={formData.martialArtType || 'Karate'}
              setMartialArtType={(type) => updateFormData('martialArtType', type)}
              martialArtStyle={formData.martialArtStyle || 'Kyokushin'}
              setMartialArtStyle={(style) => updateFormData('martialArtStyle', style)}
              joinCode={formData.joinCode || ''}
              setJoinCode={(code) => updateFormData('joinCode', code)}
            />
          );
        }
        return (
          <DojoCodeStep
            dojoCode={formData.dojoCode || ''}
            setDojoCode={(code) => {
              updateFormData('dojoCode', code);
              // Clear previous validation status when code changes
              if (code === '') {
                updateFormData('dojoCodeValid', false);
                setDojoCodeError('');
                setFormData(prev => ({ ...prev, studentDojoName: '' }));
              }
            }}
            isValid={formData.dojoCodeValid || false}
            setIsValid={(valid) => updateFormData('dojoCodeValid', valid)}
            dojoName={formData.studentDojoName || ''}
            setDojoName={(name) => updateFormData('studentDojoName', name)}
            error={dojoCodeError}
            setError={setDojoCodeError}
          />
        );

      case 4: // Emergency Contact & Medical Information
        return <EmergencyContactStep formData={formData} updateFormData={updateFormData} />;

      case 5: // Martial Arts Background
        return <MartialArtsBackgroundStep formData={formData} updateFormData={updateFormData} />;

      case 6: // Disclaimer
        return <DisclaimerStep formData={formData} updateFormData={updateFormData} />;

      default:
        return null;
    }
  };

  return (
    <div className="wh-app min-h-screen bg-background p-4 sm:py-10">
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-2xl">
          <CardHeader className="text-center relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4"
              onClick={() => navigate('/login')}
            >
              <X className="h-4 w-4" />
            </Button>
            <CardTitle className="text-2xl font-semibold text-foreground flex items-center justify-center gap-2">
              <UserPlus className="h-6 w-6" />
              {formData.role === 'admin' ? uiText("Administrator ") : ''}{uiText("Registration - WarriorHub")}</CardTitle>
            <CardDescription>
              {uiText("Join our martial arts community")}</CardDescription>
          </CardHeader>

          <CardContent>
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">{uiText("Step")}{' '}{currentStep} {' '}{uiText("of")}{' '}{totalSteps}</span>
                <span className="text-sm text-muted-foreground">
                  {currentStep === 1 && uiText("Personal Information")}
                  {currentStep === 2 && uiText("Account Type")}
                  {currentStep === 3 && (formData.role === 'admin' ? uiText("Dojo Setup") : uiText("Dojo Code"))}
                  {currentStep === 4 && uiText("Emergency Contact & Medical")}
                  {currentStep === 5 && uiText("Martial Arts Background")}
                  {currentStep === 6 && uiText("Liability Waiver")}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Display general registration error if any */}
              {registrationError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                  <strong className="font-bold">{uiText("Error!")}</strong>
                  <span className="block sm:inline ml-2">{registrationError}</span>
                </div>
              )}
              {renderStep()}

              <div className="flex justify-between pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                >
                  {uiText("Previous")}</Button>

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {uiText("Next")}</Button>
                ) : (
                  <Button
                    type="submit"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={!formData.disclaimerAgreed || !formData.digitalSignature || isSubmitting}
                  >
                    <FileCheck className="h-4 w-4 mr-2" />
                    {isSubmitting ? uiText("Submitting...") : uiText("Complete Registration")}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentRegistrationForm;
